import { Input, Text, ToggleButton } from '@fluentui/react-components';
import {
  Attach24Regular,
  Dismiss24Regular,
  Send24Regular,
  Warning24Regular,
} from '@fluentui/react-icons';
import { FileItem } from '@mnspro-cloud/services';
import cn from 'classnames';
import React, { KeyboardEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getEditableDataType } from '../../Assignments/Assignments.utils';
import { Button } from '../../Button';
import { useChatDraft } from '../drafts';
import './ChatInput.scss';
import { ChatCitedMessage, ChatInputCitedMessage } from './ChatInputCitedMessage';

export interface ChatInputProps {
  onSend: (message: string, isImportant: boolean) => void;
  /**
   * Set to true, to disable send button
   */
  disableSend?: boolean;
  replyTo?: ChatCitedMessage;
  onRemoveReplyClick: () => void;
  onAddAttachmentClick?: () => void;
  onRemoveAttachmentClick?: () => void;
  /**
   * File to attach to message
   */
  messageAttachment: FileItem | null;
  /**
   * Show the "important message" toggle button?
   */
  hideImportant?: boolean;
  /**
   * When provided, the typed message text and "important" flag are persisted as
   * a session-scoped draft keyed by this thread id, so they survive navigating
   * away and reopening the chat. When omitted, the input keeps ephemeral local
   * state (no draft).
   */
  draftThreadId?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disableSend,
  replyTo,
  onRemoveReplyClick,
  onAddAttachmentClick,
  onRemoveAttachmentClick,
  messageAttachment,
  hideImportant,
  draftThreadId,
}) => {
  const { t } = useTranslation();
  const [draft, patchDraft] = useChatDraft(draftThreadId);
  const message = draft.text;
  const isImportant = draft.isImportant;

  const doSend = useCallback(() => {
    if (disableSend || message.trim() === '') {
      return;
    }

    onSend(message, isImportant);
    patchDraft({ text: '', isImportant: false });
  }, [onSend, disableSend, message, isImportant, patchDraft]);

  const handleKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key.toUpperCase() === 'ENTER') {
        doSend();
      }
    },
    [doSend],
  );

  const handleMessageInputChange = (value: string) => patchDraft({ text: value });

  const messageInput = useMemo(
    () => (
      <Input
        appearance="filled-darker"
        type="text"
        placeholder={t('classbook:chat:text_input_placeholder:enter_new_message', {
          defaultValue: 'Eine neue Nachricht eingeben',
        })}
        value={message}
        onChange={(_, data) => handleMessageInputChange(data.value)}
        onKeyUp={handleKeyUp}
        data-testid="input"
        className="chatInputField"
        maxLength={8000}
      />
    ),
    [message, handleKeyUp, messageAttachment, isImportant, replyTo, t],
  );

  return (
    <div
      className={cn('ChatInput', {
        'ChatInput--important': isImportant,
        'ChatInput--reply': replyTo !== undefined,
        'ChatInput--attachment': messageAttachment !== null,
      })}
    >
      {(replyTo || messageAttachment || isImportant) && (
        <div className="citationInput">
          {isImportant && (
            <Text className="importantLabel">
              {t('chat_important', { defaultValue: 'Wichtig!' })}
            </Text>
          )}
          {replyTo && (
            <ChatInputCitedMessage originalMessage={replyTo} onCloseClick={onRemoveReplyClick} />
          )}
          {messageAttachment && (
            <div className="chatMessageAttachmentWrapper">
              <div className="chatMessageAttachment">
                <span className="attachmentIcon">
                  {getEditableDataType(messageAttachment.name || '', false)}
                </span>
                <Text className="attachmentName">{messageAttachment.name || ''}</Text>
                <Button
                  appearance="transparent"
                  icon={<Dismiss24Regular />}
                  onClick={onRemoveAttachmentClick}
                  aria-label={t('classbook:chat:remove_attachment', {
                    defaultValue: 'Anhang entfernen',
                  })}
                />
              </div>
            </div>
          )}
          {messageInput}
        </div>
      )}
      {!replyTo && !messageAttachment && !isImportant && messageInput}
      <div className="msgToolbar">
        <div className="msgToolbarLeft">
          {onAddAttachmentClick && (
            <Button
              appearance="transparent"
              icon={<Attach24Regular />}
              onClick={onAddAttachmentClick}
              aria-label={t('classbook:chat:add_attachment', { defaultValue: 'Anhang hinzufügen' })}
            />
          )}
          {!hideImportant && (
            <ToggleButton
              icon={<Warning24Regular />}
              appearance="transparent"
              onClick={() => patchDraft({ isImportant: !isImportant })}
              // Mark as important does not work on replies
              disabled={replyTo !== undefined}
              aria-label={t('classbook:chat:mark_important', {
                defaultValue: 'Als wichtig markieren',
              })}
            />
          )}
        </div>
        <Button
          appearance="transparent"
          icon={<Send24Regular />}
          disabled={disableSend || message.trim() === ''}
          onClick={doSend}
          data-testid="sendBtn"
          aria-label={t('classbook:chat:send_message', { defaultValue: 'Nachricht senden' })}
        />
      </div>
    </div>
  );
};
